import { useCountry } from '../useCountry'
import { Country, SearchParameters } from '../../constants'
import { useOpportunities } from '../services/opportunities'
import { useAppointmentLocationState } from '../useAppointmentLocationState'
import { useSearchParameters } from '../useSearchParameters'

const toStringParam = (v: string | string[] | undefined): string | undefined => (v === undefined ? undefined : Array.isArray(v) ? v[0] : v)

export type UseResolvedShopGidResult = {
  shopGid: string | undefined
  isLoading: boolean
}

/**
 * Resolves the shop GID from URL/state or from opportunity when shop_gid is absent.
 * Resolution order: searchParams.shop_gid ?? appointmentState.shop_gid ?? from opportunity (by opportunity_gid + customer_gid + country).
 */
const useResolvedShopGid = (): UseResolvedShopGidResult => {
  const searchParams = useSearchParameters()
  const appointmentState = useAppointmentLocationState()

  const country = useCountry()
  const shopGidFromSearch = toStringParam(searchParams[SearchParameters.SHOP_GID])
  const shopGidFromState = appointmentState?.shop_gid
  const opportunityGid = toStringParam(searchParams[SearchParameters.OPPORTUNITY_GID]) ?? appointmentState?.opportunity_gid
  const customerGid = toStringParam(searchParams[SearchParameters.CUSTOMER_GID]) ?? appointmentState?.customer_gid

  const hasShopGidFromUrlOrState = Boolean(shopGidFromSearch ?? shopGidFromState)
  const canResolveFromOpportunity = Boolean(opportunityGid && customerGid && country)

  const { data: opportunities, isLoading: isOpportunitiesLoading } = useOpportunities(
    {
      customerGid: canResolveFromOpportunity ? customerGid : undefined,
      countryCode: country as Country,
      limit: 10,
    },
    { enabled: !hasShopGidFromUrlOrState && canResolveFromOpportunity }
  )

  if (hasShopGidFromUrlOrState) {
    return {
      shopGid: shopGidFromSearch ?? shopGidFromState,
      isLoading: false,
    }
  }

  if (!canResolveFromOpportunity) {
    return { shopGid: undefined, isLoading: false }
  }

  const opportunity = opportunities?.find((o) => o.salesforceId === opportunityGid)
  const resolvedShopGid = opportunity?.partnerShopSalesforceId

  return {
    shopGid: resolvedShopGid,
    isLoading: isOpportunitiesLoading,
  }
}

export { useResolvedShopGid }
